// Polyfill for TextEncoder, which's needed for the cuid2 package
import 'fast-text-encoding';
import {createId} from '@paralleldrive/cuid2';
import {isNil} from 'ramda';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  regionalCostsInitialState,
  countryCostData,
  initCountrySalaryDefinition,
  initCountryDriverConceptType,
  initialCountrySpecificCostData,
  initialCountryYearSpecificCostData
} from './data';
import {
  CountryCostYear,
  RegionalCostsBusTypeCost,
  RegionalCostsDriverCost,
  RegionalCostsVehicleCost
} from './types';

const regionalCostsSlice = createSlice({
  name: 'regional-costs',
  initialState: regionalCostsInitialState,
  reducers: {
    countriesCostYearsFetched(
      state,
      {payload}: PayloadAction<CountryCostYear[]>
    ) {
      state.countriesCostYears = payload;
    },

    addedNewYear: (
      state,
      {
        payload
      }: PayloadAction<{
        countryId: string;
        year: number;
        busCostEntry: RegionalCostsVehicleCost;
        busTypeCostEntries: RegionalCostsBusTypeCost[];
        driverCostEntry: RegionalCostsDriverCost;
        qualityChecked: boolean;
      }>
    ) => {
      const {
        countryId,
        year,
        busCostEntry,
        busTypeCostEntries,
        driverCostEntry,
        qualityChecked
      } = payload;

      const countriesCostYears =
        state.countriesCostYears?.map(countryCostYear => {
          if (countryCostYear.countryId === countryId) {
            return {
              countryId: countryCostYear.countryId,
              years: [...countryCostYear.years, year]
            };
          }
          return countryCostYear;
        }) ?? null;
      const countryDriverConceptTypes = state.countryDriverConceptTypes;
      return {
        ...state,
        ...countryCostData,
        countriesCostYears,
        countryDriverConceptTypes,
        vehicleCost: busCostEntry,
        vehicleTypeCosts: {
          list: busTypeCostEntries,
          vehicleTypeCost: null
        },
        driverCost: driverCostEntry,
        qualityChecked
      };
    },
    deletedNewYear: (
      state,
      {payload}: PayloadAction<{countryId: string; year: number}>
    ) => {
      const {countryId, year} = payload;
      const countriesCostYears =
        state.countriesCostYears?.map(countryCostYear => {
          if (countryCostYear.countryId === countryId) {
            return {
              countryId: countryCostYear.countryId,
              years: countryCostYear.years.filter(y => y !== year)
            };
          }
          return countryCostYear;
        }) ?? null;

      return {
        ...state,
        ...countryCostData,
        countriesCostYears
      };
    },
    qualityCheckedCountryCostEntryYear(
      state,
      {payload: {qualityChecked}}: PayloadAction<{qualityChecked: boolean}>
    ) {
      return {
        ...state,
        qualityChecked
      };
    },

    // Country Cost Entry
    receivedCountryCostEntry: (state, {payload}: PayloadAction<any>) => {
      return {
        ...state,
        vehicleCost: payload?.busCostEntry,
        vehicleTypeCosts: {
          list: payload?.busTypeCostEntries
        },
        driverCost: payload?.driverCostEntry,
        qualityChecked: payload?.qualityChecked
      };
    },
    resetCountrySpecificData: state => {
      return {
        ...state,
        ...initialCountrySpecificCostData,
        ...initialCountryYearSpecificCostData
      };
    },
    resetCountryYearSpecificData: state => {
      return {
        ...state,
        ...initialCountryYearSpecificCostData
      };
    },
    receivedCountryVehicleCostAction: (
      state,
      {payload}: PayloadAction<RegionalCostsVehicleCost>
    ) => {
      return {
        ...state,
        vehicleCost: payload
      };
    },
    receivedCountryDriverCost: (
      state,
      {payload}: PayloadAction<RegionalCostsDriverCost>
    ) => {
      return {
        ...state,
        driverCost: payload
      };
    },
    addToCountryVehicleTypeCostAction: (
      state,
      {payload}: PayloadAction<RegionalCostsBusTypeCost>
    ) => {
      const list = [...(state.vehicleTypeCosts.list ?? []), payload];
      return {
        ...state,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list
        }
      };
    },
    updateVehicleTypeCostAction: (
      state,
      {payload}: PayloadAction<RegionalCostsBusTypeCost>
    ) => {
      const list =
        state.vehicleTypeCosts.list?.map(vehicleTypeCost =>
          payload.id === vehicleTypeCost.id ? payload : vehicleTypeCost
        ) ?? [];

      return {
        ...state,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list
        }
      };
    },
    deleteFromVehicleTypeCostAction: (
      state,
      {payload}: PayloadAction<RegionalCostsBusTypeCost>
    ) => {
      const list =
        state.vehicleTypeCosts?.list?.filter(vehicleTypeCost => {
          return payload.id !== vehicleTypeCost.id;
        }) ?? [];

      return {
        ...state,
        vehicleTypeCosts: {
          ...state.vehicleTypeCosts,
          list
        }
      };
    },

    // Country Driver Concept Type
    setCountryDriverConceptType: (state, {payload}: PayloadAction<any>) => {
      return {
        ...state,
        countryDriverConceptTypes: {
          ...state.countryDriverConceptTypes,
          countryDriverConceptType: isNil(payload)
            ? initCountryDriverConceptType
            : payload
        }
      };
    },
    resetCountryDriverConceptType: state => {
      return {
        ...state,
        countryDriverConceptTypes: {
          ...state.countryDriverConceptTypes,
          countryDriverConceptType: null
        }
      };
    },
    receivedCountryDriverConceptTypes: (
      state,
      {payload}: PayloadAction<any>
    ) => {
      return {
        ...state,
        countryDriverConceptTypes: {
          ...state.countryDriverConceptTypes,
          list: payload
        }
      };
    },
    deleteFromCountryDriverConceptTypes: (
      state,
      {payload}: PayloadAction<any>
    ) => {
      const list =
        state.countryDriverConceptTypes?.list?.filter(
          countryDriverConceptType => payload.id !== countryDriverConceptType.id
        ) ?? [];

      return {
        ...state,
        countryDriverConceptTypes: {
          ...state.countryDriverConceptTypes,
          list
        }
      };
    },
    updateCountryDriverConceptTypes: (state, {payload}: PayloadAction<any>) => {
      const list =
        state.countryDriverConceptTypes?.list?.map(countryDriverConceptType => {
          return payload.id === countryDriverConceptType.id
            ? payload
            : countryDriverConceptType;
        }) ?? [];

      return {
        ...state,
        countryDriverConceptTypes: {
          ...state.countryDriverConceptTypes,
          list
        }
      };
    },
    addToCountryDriverConceptTypes: (state, {payload}: PayloadAction<any>) => {
      const list = [...(state.countryDriverConceptTypes?.list ?? []), payload];

      return {
        ...state,
        countryDriverConceptTypes: {
          ...state.countryDriverConceptTypes,
          list
        }
      };
    },

    // Country Salary Definition
    addCountrySalaryDefinition: {
      reducer: (state, {payload}: PayloadAction<any>) => {
        return {
          ...state,
          countrySalaryDefinition: {
            ...state.countrySalaryDefinition,
            list: [
              ...state.countrySalaryDefinition.list,
              ...[
                {
                  ...initCountrySalaryDefinition,
                  salaryType: payload.salaryType,
                  dayOfWeek: payload.dayOfWeek,
                  id: createId()
                }
              ]
            ]
          }
        };
      },
      prepare: (salaryType, dayOfWeek) => ({
        payload: {
          salaryType,
          dayOfWeek
        }
      })
    },
    updateCountrySalaryDefinition: (state, {payload}: PayloadAction<any>) => {
      return {
        ...state,
        countrySalaryDefinition: {
          ...state.countrySalaryDefinition,
          list: [...state.countrySalaryDefinition.list].map(salaryDefinition =>
            salaryDefinition.id === payload.id ? payload : salaryDefinition
          )
        }
      };
    },
    deleteCountrySalaryDefinition: (state, {payload}: PayloadAction<any>) => {
      return {
        ...state,
        countrySalaryDefinition: {
          ...state.countrySalaryDefinition,
          list: [...state.countrySalaryDefinition.list].filter(
            salaryDefinition => salaryDefinition.id !== payload.id
          )
        }
      };
    },

    receivedCountrySalaryDefinitions: (
      state,
      {payload}: PayloadAction<any[]>
    ) => {
      return {
        ...state,
        countrySalaryDefinition: {
          ...state.countrySalaryDefinition,
          list: payload.map(salaryDefinition => ({
            ...salaryDefinition,
            id: createId()
          }))
        }
      };
    },
    driverSalaryDefinitionsSaved: state => {
      return {
        ...state,
        countrySalaryDefinition: {
          ...state.countrySalaryDefinition,
          messages: ['Saved']
        }
      };
    },
    driverSalaryDefinitionsErrors: (state, {payload}: PayloadAction<any>) => {
      return {
        ...state,
        countrySalaryDefinition: {
          ...state.countrySalaryDefinition,
          messages: payload
        }
      };
    }
  }
});

export const {
  countriesCostYearsFetched,
  addedNewYear,
  deletedNewYear,
  qualityCheckedCountryCostEntryYear,

  // Country Cost Entry
  receivedCountryCostEntry,
  resetCountrySpecificData,
  resetCountryYearSpecificData,
  receivedCountryVehicleCostAction,
  receivedCountryDriverCost,
  addToCountryVehicleTypeCostAction,
  updateVehicleTypeCostAction,
  deleteFromVehicleTypeCostAction,

  // Country Driver Concept Type
  setCountryDriverConceptType,
  resetCountryDriverConceptType,
  receivedCountryDriverConceptTypes,
  deleteFromCountryDriverConceptTypes,
  updateCountryDriverConceptTypes,
  addToCountryDriverConceptTypes,

  // Country Salary Definition
  addCountrySalaryDefinition,
  updateCountrySalaryDefinition,
  deleteCountrySalaryDefinition,
  receivedCountrySalaryDefinitions,
  driverSalaryDefinitionsSaved,
  driverSalaryDefinitionsErrors
} = regionalCostsSlice.actions;

export default regionalCostsSlice.reducer;
